import { hexStringToBuffer } from '@iotize/common/byte-converter';
import {
  getConverterFromVariableConfig,
  getConverterFromVariableType,
} from '@iotize/tap/data';
import {
  VariableMetaConverter,
  VariableType,
  VariableTypeConverter,
} from '@iotize/tap/service/all';
import { debug } from '../../lib/debug';

import { GET, POST, PUT } from '../annotations';
import { uint16, uint32, uint8, variableType } from '../converters';
import { TapServerError } from '../tap-server-error';
import { CallContext, VirtualTap } from '../virtual-tap-server';

export class VariableService {
  constructor(public server: VirtualTap) {}

  @GET({
    path: '/1029/{variableId}/0',
    responseType: uint32,
  })
  public getVariableAddress() {
    throw TapServerError.notAvailable();
  }

  @PUT({
    path: '/1029/{variableId}/0',
    payloadType: uint32,
  })
  public putVariableAddress(context: CallContext<number>) {
    const variable = this.server.configExplorer.getVariableById(
      context.pathParameters.variableId
    );
    variable.address = context.body;
  }

  @POST({
    path: '/1029/{variableId}/65535',
    payloadType: uint8,
    configModeRequired: true,
    auth: ['admin'],
  })
  public createVariable(context: CallContext<number>) {
    const metaSize = context.body;
    this.server.configExplorer.createVariable(
      context.pathParameters.variableId,
      metaSize || 0
    );
  }

  @GET({
    path: '/1029/{variableId}/1',
    responseType: variableType,
  })
  public getVariableType(context: CallContext<void>) {
    const variable = this.server.configExplorer.getVariableById(
      context.pathParameters.variableId
    );
    return {
      data:
        variable.dataType === undefined
          ? VariableType.Data.UINT8
          : VariableType.Data[variable.dataType],
      domain:
        variable.domain === undefined
          ? VariableType.Domain.TARGET
          : VariableType.Domain[variable.domain],
    };
  }

  @PUT({
    path: '/1029/{variableId}/1',
  })
  public putVariableType(context: CallContext<Uint8Array>) {
    const variable = this.server.configExplorer.getVariableById(
      context.pathParameters.variableId
    );
    const value = new VariableTypeConverter().decode(context.body);
    variable.dataType = VariableType.Data[value.data];
    variable.domain = VariableType.Domain[value.domain];
  }

  @GET({
    path: '/1029/{variableId}/2',
    responseType: uint8,
  })
  public getArrayLength(context: CallContext<void>) {
    const length = this.server.configExplorer.getVariableById(
      context.pathParameters.variableId
    ).length;
    if (length === undefined) {
      return 0;
    } else {
      return length;
    }
  }

  @PUT({
    path: '/1029/{variableId}/2',
    payloadType: uint8,
  })
  public putArrayLength(context: CallContext<number>) {
    const variable = this.server.configExplorer.getVariableById(
      context.pathParameters.variableId
    );
    variable.length = context.body;
  }

  @GET({
    path: '/1029/{variableId}/4',
  })
  public getVariableValue(context: CallContext<void>): Uint8Array {
    const variableId = context.getPathParameter('variableId');
    if (variableId === 0xffff) {
      return this.server.targetCom.getProfileValues(this.server.sessionState);
    } else {
      // const converter = this._getConverterForVariable(variableId);
      const value = this.server.targetCom.getValue(variableId);
      // console.log('get variable value', variableId, value);
      return value;
    }
  }

  @PUT({
    path: '/1029/{variableId}/4',
  })
  public putVariableValue(context: CallContext<Uint8Array>) {
    const variableId = context.getPathParameter('variableId');
    const converter = this._getConverterForVariable(variableId);
    if (!context.body || context.body.length === 0) {
      debug(`Missing request body`);
      throw TapServerError.badRequest();
    }
    // TO be sure payload is valid...
    converter.decode(context.body);

    return this.server.targetCom.setValue(variableId, context.body);
  }

  @POST({
    path: '/1029/{variableId}/5',
  })
  public postVariableValue(context: CallContext<Uint8Array>) {
    return this.putVariableValue(context);
  }

  @GET({
    path: '/1029/{variableId}/6',
    responseType: uint16,
  })
  public getBundleId(context: CallContext<void>): number {
    const variableId = context.getPathParameter('variableId');
    const info = this.server.configExplorer.getVariableWithMetaById(variableId);
    if (!info.bundle) {
      return 0; // TODO check ?
    }
    return info.bundle.id;
  }

  @PUT({
    path: '/1029/{variableId}/6',
    payloadType: uint16,
    auth: ['admin', 'supervisor'],
  })
  public putBundleId(context: CallContext<number>): void {
    const variableId = context.getPathParameter('variableId');
    this.server.configExplorer.changeVariableBundle(variableId, context.body);
  }

  @GET({
    path: '/1029/{variableId}/8',
  })
  public getRawMeta(context: CallContext<void>): Uint8Array {
    const variableId = context.getPathParameter('variableId');
    const info = this.server.configExplorer.getVariableWithMetaById(variableId);
    if (typeof info.variable.rawMeta === 'string') {
      return hexStringToBuffer(info.variable.rawMeta);
    } else {
      return info.variable.rawMeta || new Uint8Array();
    }
  }

  @PUT({
    path: '/1029/{variableId}/8',
  })
  public putRawMeta(context: CallContext<Uint8Array>): void {
    const variableId = context.getPathParameter('variableId');
    const info = this.server.configExplorer.getVariableById(variableId);
    let maxMetaDataLength: number;
    if (info.metaDataLength === undefined) {
      maxMetaDataLength = info.rawMeta?.length || 0;
    } else {
      maxMetaDataLength = info.metaDataLength;
    }
    if (context.body.length > maxMetaDataLength) {
      console.warn(
        `Variable meta data length maximum size is ${maxMetaDataLength} bytes but trying to write ${context.body.length} bytes`
      );
      throw TapServerError.notAcceptable(); // TODO check same error as TAP?
    }
    info.rawMeta = context.body;
  }

  private _getConverterForVariable(variableId: number) {
    const variableConfig =
      this.server.configExplorer.getVariableById(variableId);
    const converter = getConverterFromVariableConfig(variableConfig);
    if (!converter) {
      throw TapServerError.internalError(
        `Cannot find converter for type: ${variableConfig.type}`
      );
    }
    return converter;
  }
}
